'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import PersonalPlanHeader from '@/page-components/PersonalPlan/PersonalPlanHeader';
import apiClient from '@/apiClient';
import {
  FormStatusSurvey,
  SurveySessionResponse,
} from '@/page-components/YourDetails/YourDetails.type';
import { Locale } from '@/i18n/config';
import { setUserLocale } from '@/i18n/locale';

export default function Page() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const typeformId = process.env.NEXT_PUBLIC_TYPEFORM_ID;

  const [surveyId, setSurveyId] = useState<string>('');

  const getLocale = async () => {
    try {
      const res = await fetch('/server/getBrowserLocale', { cache: 'no-store' });
      const data = await res.json();
      return data.locale || 'en';
    } catch {
      return 'en';
    }
  };

  useEffect(() => {
    const generateSurveySession = async () => {
      const sessionIdFromUrl = searchParams.get('form_survey_id');
      const localeFromUrl = searchParams.get('locale');
      const detectedLocale = await getLocale();

      if (!localeFromUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('locale', detectedLocale);
        await setUserLocale(detectedLocale as Locale);
        router.replace(url.toString());
      }

      if (sessionIdFromUrl) {
        try {
          const res = await apiClient.getResponse<SurveySessionResponse>(
            `survey/session/${sessionIdFromUrl}`,
          );
          const status = res.response?.data?.status;

          if (res.status === 200 && status === FormStatusSurvey.PROCESSING) {
            setSurveyId(sessionIdFromUrl);
            return;
          }
        } catch (err) {
          console.warn('Error fetching existing session', err);
        }
      }

      try {
        const createRes = await apiClient.postResponse<SurveySessionResponse>(
          'survey/session',
        );

        if (createRes.status === 200) {
          const newId = createRes.response.data.uuid;

          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('form_survey_id', newId);
          newUrl.searchParams.set('locale', detectedLocale);

          router.replace(newUrl.toString());
          setSurveyId(newId);
        }
      } catch (err) {
        console.error('Failed to create session', err);
      }
    };

    generateSurveySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='surveyPage'>
      <PersonalPlanHeader stage='start' />

      {surveyId && (
        <section className='surveyContainer'>
          <div data-tf-live={typeformId} />
          <Script
            src='https://embed.typeform.com/next/embed.js'
            strategy='afterInteractive'
          />
        </section>
      )}
    </div>
  );
}
